import { useState, useCallback, useRef, useEffect } from 'react';
import axios from '../../services/api';
import { validateFile, createPreviewUrl, cleanupBlobUrl } from '../utils/fileUtils';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants';
import { useI18n } from '../../contexts/I18nContext';

/**
 * 头像上传自定义Hook
 * @param {Function} onSuccess - 上传成功回调
 * @param {Function} onError - 上传失败回调
 */
export const useAvatarUpload = (onSuccess, onError) => {
  const { t } = useI18n();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const abortControllerRef = useRef(null);
  const previewUrlRef = useRef(previewUrl);
  previewUrlRef.current = previewUrl;

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      // 取消正在进行的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort('Upload canceled due to component unmount.');
      }
      // 清理预览URL
      if (previewUrlRef.current) {
        cleanupBlobUrl(previewUrlRef.current);
      }
    };
  }, []);

  // 清理预览URL
  const cleanupPreview = useCallback(() => {
    if (previewUrlRef.current) {
      cleanupBlobUrl(previewUrlRef.current);
    }
    setPreviewUrl('');
  }, []);

  // 设置错误信息
  const setErrorMessage = useCallback((message) => {
    setError(message);
    setSuccessMessage('');
    if (onError) {
      onError(message);
    }
  }, [onError]); // 移除不必要的t依赖项

  // 上传文件
  const uploadFile = useCallback(async (file) => {
    setIsUploading(true);
    setError('');

    // 创建取消控制器
    abortControllerRef.current = new AbortController();

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post('/users/me/avatar', formData, {
        signal: abortControllerRef.current.signal,
        timeout: 30000 // 30秒超时
      });

      const userData = response.data;
      
      // 清理预览URL
      cleanupPreview();
      
      // 设置成功消息
      setSuccessMessage(t(SUCCESS_MESSAGES.UPLOAD_SUCCESS));
      
      // 通知父组件
      if (onSuccess) {
        onSuccess(userData.avatar);
      }

    } catch (error) {
      // 如果是取消错误，静默处理
      if (error.name === 'AbortError' || 
          error.name === 'CanceledError' || 
          error.code === 'ERR_CANCELED' ||
          (error.message && (error.message.includes('canceled') || error.message.includes('aborted')))) {
        console.log('Upload canceled:', error.message || 'No cancellation message provided');
        return;
      }
      
      console.error('Upload error:', error);
      
      // 设置错误消息
      if (error.response?.status === 413) {
        setErrorMessage(ERROR_MESSAGES.FILE_TOO_LARGE);
      } else if (error.response?.status === 415) {
        setErrorMessage(ERROR_MESSAGES.INVALID_FILE_TYPE);
      } else if (error.code === 'ECONNABORTED') {
        setErrorMessage(ERROR_MESSAGES.UPLOAD_TIMEOUT);
      } else {
        setErrorMessage(error.response?.data?.message || ERROR_MESSAGES.UPLOAD_FAILED);
      }
      
      // 通知父组件
      if (onError) {
        onError(error);
      }
    } finally {
      setIsUploading(false);
      abortControllerRef.current = null;
    }
  }, [cleanupPreview, onSuccess, onError, setErrorMessage, t]); // 移除不必要的setSuccessInfo依赖项

  // 处理文件选择
  const handleFileSelect = useCallback(async (file) => {
    if (!file) return;

    // 清除之前的消息
    setError('');
    setSuccessMessage('');

    // 验证文件
    const validation = validateFile(file);
    if (!validation.isValid) {
      setErrorMessage(validation.error);
      return;
    }

    try {
      // 创建预览
      const preview = await createPreviewUrl(file);
      cleanupPreview(); // 清理旧的预览
      setPreviewUrl(preview);

      // 开始上传
      await uploadFile(file);
    } catch (error) {
      console.error('File processing error:', error);
      setErrorMessage('文件处理失败，请重试');
    }
  }, [cleanupPreview, uploadFile, setErrorMessage]);

  // 取消上传
  const cancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // 清理资源
  const cleanup = useCallback(() => {
    cleanupPreview();
    cancelUpload();
    setError('');
    setSuccessMessage('');
  }, [cleanupPreview, cancelUpload]);

  return {
    isUploading,
    error,
    previewUrl,
    successMessage,
    handleFileSelect,
    cancelUpload,
    cleanup
  };
};